import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import { logUsageEvent } from '../../src/lib/logger';
import { api } from '../../src/services/api';
import { useToast } from '../../src/contexts/ToastContext';
import { Profile } from '../../types';
import { PenTool, Users, Loader2, Save, CheckCircle2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { TERMO_TESTEMUNHA_1_KEY, TERMO_TESTEMUNHA_2_KEY, TERMO_PROCURADOR_KEY } from '../../src/lib/termoSignatarios';

const TermoSignatariosView = () => {
    const { addToast } = useToast();
    const { user } = useAuth();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [users, setUsers] = useState<Profile[]>([]);
    const [testemunha1Id, setTestemunha1Id] = useState('');
    const [testemunha2Id, setTestemunha2Id] = useState('');
    const [procuradorId, setProcuradorId] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [profiles, { data: settings }] = await Promise.all([
                api.auth.listProfiles(),
                supabase
                    .from('system_settings')
                    .select('key, value')
                    .in('key', [TERMO_TESTEMUNHA_1_KEY, TERMO_TESTEMUNHA_2_KEY, TERMO_PROCURADOR_KEY])
            ]);

            setUsers(profiles.filter(u => u.status !== 'inactive'));

            const settingsMap: Record<string, string> = {};
            (settings || []).forEach((s: { key: string; value: string }) => { settingsMap[s.key] = s.value; });
            setTestemunha1Id(settingsMap[TERMO_TESTEMUNHA_1_KEY] || '');
            setTestemunha2Id(settingsMap[TERMO_TESTEMUNHA_2_KEY] || '');
            setProcuradorId(settingsMap[TERMO_PROCURADOR_KEY] || '');
        } catch (error) {
            console.error('Error loading signatarios config:', error);
            addToast('Erro ao carregar configurações de signatários.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const isElegivelTestemunha = (u: Profile) => !!(u.email_corporativo?.trim() && u.cpf?.trim());
    // Procurador também precisa de CPF: os documentos (termo, distrato, notificações) imprimem o CPF do representante
    const isElegivelProcurador = (u: Profile) => !!(u.email_corporativo?.trim() && u.cpf?.trim());

    const testemunhasElegiveis = useMemo(() => users.filter(isElegivelTestemunha), [users]);
    const testemunhasInelegiveis = useMemo(() => users.filter(u => !isElegivelTestemunha(u)), [users]);
    const procuradoresElegiveis = useMemo(() => users.filter(isElegivelProcurador), [users]);

    const getUser = (id: string) => users.find(u => u.id === id);

    const handleSave = async () => {
        if (!testemunha1Id || !testemunha2Id || !procuradorId) {
            addToast('Selecione as duas testemunhas e o procurador.', 'error');
            return;
        }
        if (testemunha1Id === testemunha2Id) {
            addToast('As duas testemunhas devem ser pessoas diferentes.', 'error');
            return;
        }

        setSaving(true);
        setSuccess(false);
        try {
            const { error } = await supabase
                .from('system_settings')
                .upsert([
                    { key: TERMO_TESTEMUNHA_1_KEY, value: testemunha1Id, updated_at: new Date() },
                    { key: TERMO_TESTEMUNHA_2_KEY, value: testemunha2Id, updated_at: new Date() },
                    { key: TERMO_PROCURADOR_KEY, value: procuradorId, updated_at: new Date() }
                ]);

            if (error) throw error;
            setSuccess(true);
            addToast('Signatários do termo salvos com sucesso.', 'success');
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'termo_signatarios', acao: 'salvar_signatarios_termo', origem: 'acao' });
            setTimeout(() => setSuccess(false), 3000);
        } catch (error) {
            console.error('Error saving signatarios config:', error);
            addToast('Erro ao salvar signatários.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const renderTestemunhaSelect = (
        label: string,
        value: string,
        onChange: (id: string) => void,
        otherSelectedId: string
    ) => {
        const selected = getUser(value);
        return (
            <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
                <select
                    className="w-full p-2.5 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                >
                    <option value="">Selecione...</option>
                    {testemunhasElegiveis.map(u => (
                        <option key={u.id} value={u.id} disabled={u.id === otherSelectedId}>
                            {u.full_name}{u.departamento ? ` — ${u.departamento}` : ''}
                        </option>
                    ))}
                </select>
                {selected && (
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                        CPF: {selected.cpf} · E-mail corporativo: {selected.email_corporativo}
                    </p>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-16">
                <Loader2 size={28} className="animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <PenTool size={22} className="text-primary" />
                    Assinaturas dos Termos
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Padrão de procurador e testemunhas para <strong>todos os documentos</strong> (termo de adesão, distrato,
                    aditivo, advertência e notificação extrajudicial). Cada modal permite trocar por documento.
                </p>
            </div>

            {/* Testemunhas */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 space-y-4">
                <div className="flex items-center gap-2">
                    <Users size={18} className="text-primary" />
                    <h3 className="font-bold text-gray-900 dark:text-white">Testemunhas</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    As testemunhas assinam com o <strong>e-mail corporativo</strong> e precisam ter <strong>CPF</strong> cadastrado.
                    Cada testemunha aparece impressa no documento com seus próprios dados.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderTestemunhaSelect('Testemunha 1', testemunha1Id, setTestemunha1Id, testemunha2Id)}
                    {renderTestemunhaSelect('Testemunha 2', testemunha2Id, setTestemunha2Id, testemunha1Id)}
                </div>

                {testemunhasInelegiveis.length > 0 && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900 rounded-xl">
                        <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                            Usuários sem e-mail corporativo e CPF cadastrados não aparecem na lista. Cadastre esses dados em{' '}
                            <strong>Usuários e Permissões → Editar usuário</strong> para torná-los elegíveis.
                        </p>
                    </div>
                )}
            </div>

            {/* Procurador */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 space-y-4">
                <div className="flex items-center gap-2">
                    <ShieldCheck size={18} className="text-primary" />
                    <h3 className="font-bold text-gray-900 dark:text-white">Procurador</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Assina os documentos representando a empresa e recebe o e-mail do Autentique no <strong>e-mail corporativo</strong>.
                    Precisa ter <strong>CPF</strong> cadastrado (os documentos imprimem o CPF do representante).
                    Selecione alguém com poderes de representação (procuração).
                </p>

                <div className="space-y-1 md:max-w-md">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Procurador</label>
                    <select
                        className="w-full p-2.5 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                        value={procuradorId}
                        onChange={e => setProcuradorId(e.target.value)}
                    >
                        <option value="">Selecione...</option>
                        {procuradoresElegiveis.map(u => (
                            <option key={u.id} value={u.id}>
                                {u.full_name}{u.job_title ? ` — ${u.job_title}` : ''}
                            </option>
                        ))}
                    </select>
                    {procuradorId && getUser(procuradorId) && (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                            E-mail corporativo: {getUser(procuradorId)?.email_corporativo}
                        </p>
                    )}
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                        Apenas usuários com e-mail corporativo cadastrado aparecem na lista.
                    </p>
                </div>
            </div>

            <div className="flex justify-end">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2.5 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-colors flex items-center gap-2 shadow-lg shadow-primary/20 disabled:opacity-60"
                >
                    {saving ? <Loader2 size={18} className="animate-spin" /> : success ? <CheckCircle2 size={18} /> : <Save size={18} />}
                    {success ? 'Salvo!' : 'Salvar Signatários'}
                </button>
            </div>
        </div>
    );
};

export default TermoSignatariosView;
